// Add a toolkit (02-DESIGN/08-toolkits/02-the-mcp-runtime.md § what an
// instance is, § the settings document; 03-linking.md § dependencies): an
// admin registers a workload of the install's MCP runtime image with the
// server's host — and, for a redeeming scheme, the token endpoint's — as
// its egress, writes its settings document whole, and stores the client
// secret or the shared key where the runtime reads them. The onboarding note
// beside the form says what the flow cannot create: one registered client
// per toolkit per install at the provider, with this console's page for the
// toolkit as its redirect URI. It is a form in the Toolkits page's place, in
// three steps — the server, how it signs in, its tools — with what has a
// working default under *More options*, closed (01-the-console.md §
// conventions: a form).

import { useState } from "react";

import { usePage } from "../../platform/session.tsx";
import { Button, Choices, describe, Field, Notice, TextInput, useLoad } from "../../ui/components.tsx";
import { FormPage } from "../../ui/form.tsx";
import { ClientType, clientSecretPath, credentialPath, Scheme, Scope } from "../../wire/toolkit.ts";
import {
  ALL_TOOLS,
  check,
  DOCUMENT,
  egressOf,
  parseDeclaredTools,
  parseTools,
  type CredentialAttachment,
  type Settings,
} from "../../wire/toolkit-mcp.ts";
import {
  listSubject,
  registerSubject,
  SERVICE as WORKLOADS,
  type ListReply,
  type RegisterReply,
  type RegisterRequest,
} from "../../wire/workloads.ts";
import { computeCheck, computeOf } from "../settings/plan.ts";
import { PlanLink, PlanRefused, usePlan } from "../settings/PlanNotes.tsx";
import { useSurface } from "../surface.tsx";
import { redirectUriFor } from "./link.ts";

/** The runtime's placement: enough for one remote server's translation. */
const RESOURCES = { cpus: 1, memory_mib: 256 };

type Attach = "bearer" | "header" | "query";

/** AddStep is one step of *Add a toolkit*: a form longer than one screen is in steps (§ conventions: a form). */
export type AddStep = "server" | "sign-in" | "tools";

const STEP_TITLES: Record<AddStep, string> = {
  server: "The server",
  "sign-in": "How it signs in",
  tools: "Its tools",
};

/** addSteps is the steps of *Add a toolkit* for what it acts as: a toolkit that acts as nobody has nothing to sign in with. */
export function addSteps(scope: string): AddStep[] {
  return scope === Scope.none ? ["server", "tools"] : ["server", "sign-in", "tools"];
}

/**
 * stepOf is where *Add a toolkit* stands: the step shown, its number and the
 * total. The total comes from what the toolkit acts as, chosen on the first
 * step, so it is the same on every step once chosen — *of 2* for a toolkit
 * that acts as nobody, *of 3* otherwise — and the step held is clamped to
 * the steps there are.
 */
export function stepOf(
  scope: string,
  step: number,
): { at: AddStep; index: number; number: number; total: number } {
  const steps = addSteps(scope);
  const index = Math.max(0, Math.min(step, steps.length - 1));
  return { at: steps[index] ?? "server", index, number: index + 1, total: steps.length };
}

/** SignIn is what the sign-in step asks for. */
export interface SignIn {
  scope: string;
  redeeming: boolean;
  confidential: boolean;
  authorize: string;
  tokenEndpoint: string;
  clientId: string;
  audience: string;
  clientSecret: string;
  sharedKey: string;
  attach: string;
  attachName: string;
}

/**
 * signInComplete says whether the sign-in step holds what it needs to go on:
 * for linking, the provider's endpoints, the client id, the audience and a
 * confidential client's secret; for the workspace's one key, the key; and a
 * header's or a parameter's name where the key does not go as a bearer.
 */
export function signInComplete(x: SignIn): boolean {
  const filled = (v: string) => v.trim() !== "";
  if (x.redeeming) {
    if (![x.authorize, x.tokenEndpoint, x.clientId, x.audience].every(filled)) {
      return false;
    }
    if (x.confidential && x.clientSecret === "") {
      return false;
    }
  }
  if (x.scope === Scope.tenant && x.sharedKey === "") {
    return false;
  }
  return x.attach === "bearer" || filled(x.attachName);
}

export function AddToolkit({
  image,
  onClose,
  onAdded,
}: {
  image: string;
  onClose: () => void;
  onAdded: (name: string) => void;
}) {
  const s = useSurface();
  const plan = usePlan();
  const [registered] = useLoad("toolkit/registered", () =>
    s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined),
  );
  const { origin } = usePage();
  const [name, setName] = useState("");
  const [scope, setScope] = useState<string>(Scope.person);
  const [url, setUrl] = useState("");
  const [personScheme, setPersonScheme] = useState<string>(Scheme.oauthRefresh);
  const [audience, setAudience] = useState("");
  const [authorize, setAuthorize] = useState("");
  const [tokenEndpoint, setTokenEndpoint] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientType, setClientType] = useState<string>(ClientType.public);
  const [scopes, setScopes] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [sharedKey, setSharedKey] = useState("");
  const [attach, setAttach] = useState<Attach>("bearer");
  const [attachName, setAttachName] = useState("");
  const [tools, setTools] = useState(ALL_TOOLS);
  const [declared, setDeclared] = useState("");
  const [instructions, setInstructions] = useState("");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);

  const scheme: string =
    scope === Scope.person ? personScheme : scope === Scope.tenant ? Scheme.token : Scheme.none;
  const redeeming = scheme === Scheme.oauthRefresh;
  const confidential = redeeming && clientType === ClientType.confidential;
  // Before a name is typed the note shows where it goes, unencoded.
  const redirectUri =
    name.trim() === ""
      ? redirectUriFor(origin, s.tenant, "") + "<name>"
      : redirectUriFor(origin, s.tenant, name.trim());

  const compose = (): Settings | Error => {
    const settings: Settings = {
      name: name.trim(),
      scope: scope as Settings["scope"],
      url: url.trim(),
      "auth-scheme": scheme as Settings["auth-scheme"],
    };
    if (redeeming) {
      settings.audience = audience.trim();
      settings["token-exchange"] = {
        "token-endpoint": tokenEndpoint.trim(),
        "authorize-endpoint": authorize.trim(),
        "client-id": clientId.trim(),
        "client-type": clientType as ClientType,
        scopes: parseTools(scopes),
      };
    }
    if (scope === Scope.person) {
      const d = parseDeclaredTools(declared);
      if (d instanceof Error) {
        return d;
      }
      settings["declared-tools"] = d;
    } else {
      settings.tools = parseTools(tools);
    }
    if (scheme !== Scheme.none) {
      let attachment: CredentialAttachment;
      switch (attach) {
        case "bearer":
          attachment = { bearer: true };
          break;
        case "header":
          attachment = { header: attachName.trim() };
          break;
        case "query":
          attachment = { query: attachName.trim() };
          break;
      }
      settings["credential-attachment"] = attachment;
    }
    if (instructions.trim() !== "") {
      settings["setup-instructions"] = instructions.trim();
    }
    return settings;
  };

  const composed = compose();
  const problem =
    composed instanceof Error
      ? composed.message
      : (check(composed) ??
        (attach !== "bearer" && scheme !== Scheme.none && attachName.trim() === ""
          ? "credential-attachment: name the header or the query parameter"
          : confidential && clientSecret === ""
            ? "client secret: a confidential client's secret, stored where only the toolkit reads it"
            : scope === Scope.tenant && sharedKey === ""
              ? `key: the one key everyone in ${s.name} uses`
              : undefined));
  // A toolkit is a workload: the plan's room for one more, said before the
  // form is submitted (02-DESIGN/17-plans/02-features-and-limits.md § the console).
  const room = computeCheck(
    plan.tenant,
    plan.holder,
    registered.state === "ready" ? computeOf(registered.value.workloads) : undefined,
    { cpus: RESOURCES.cpus, memoryMiB: RESOURCES.memory_mib, copies: 1 },
  );
  const ready =
    busy === undefined && problem === undefined && !(composed instanceof Error) && room.refusal === undefined;

  const submit = () => {
    if (!ready || composed instanceof Error) {
      return;
    }
    setErr(undefined);
    const body: RegisterRequest = {
      name: composed.name,
      description: `A toolkit wrapping ${composed.url}`,
      image,
      resources: RESOURCES,
      egress: egressOf(composed),
      // The name it answers discovery under, vouched in its attestation (decision 0026).
      names: [composed.name],
    };
    setBusy("Adding the toolkit's workload");
    s.bus
      .call<RegisterReply>(WORKLOADS, registerSubject(), body, 60000)
      .then(async (reply) => {
        const id = reply.id;
        const after = (step: string, e: unknown) =>
          new Error(
            `${step} failed after the workload ${id} was added: ${describe(e)}. Finish it under Configuration and Secrets, or remove the workload.`,
          );
        setBusy("Writing its settings");
        try {
          await s.bus.config().set(id, DOCUMENT, JSON.stringify(composed));
        } catch (e) {
          throw after("Writing the settings", e);
        }
        if (confidential) {
          setBusy("Storing the client secret");
          try {
            await s.bus.vault().store(clientSecretPath(id), clientSecret);
          } catch (e) {
            throw after("Storing the client secret", e);
          }
        }
        if (scope === Scope.tenant) {
          setBusy("Storing the key");
          try {
            await s.bus.vault().store(credentialPath(id), JSON.stringify({ token: sharedKey }));
          } catch (e) {
            throw after("Storing the key", e);
          }
        }
        return composed.name;
      })
      .then(
        (added) => {
          setBusy(undefined);
          onAdded(added);
        },
        (e: unknown) => {
          setBusy(undefined);
          setErr(e);
        },
      );
  };

  const where = stepOf(scope, step);
  const at = where.at;
  const last = where.number === where.total;
  const stepReady =
    at === "server"
      ? name.trim() !== "" && url.trim() !== ""
      : at === "sign-in"
        ? signInComplete({
            scope,
            redeeming,
            confidential,
            authorize,
            tokenEndpoint,
            clientId,
            audience,
            clientSecret,
            sharedKey,
            attach,
            attachName,
          })
        : ready;
  const next = () => {
    if (last) {
      submit();
    } else if (stepReady) {
      setStep(where.index + 1);
    }
  };

  return (
    <FormPage
      title="Add a toolkit"
      line={`A remote MCP server, wrapped so that its tools can be called through ${s.name} — as the person who asks, as ${s.name}, or as nobody. It runs as a workload and reaches the server you name, and nothing else.`}
      back="Toolkits"
      onClose={onClose}
      foot={
        <>
          {busy !== undefined && <span className="small faint">{busy}…</span>}
          {where.index > 0 ? (
            <Button
              kind="quiet"
              onClick={() => {
                setStep(where.index - 1);
              }}
              disabled={busy !== undefined}
            >
              Back
            </Button>
          ) : (
            <Button kind="quiet" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button kind="primary" onClick={next} disabled={!stepReady}>
            {last ? (busy !== undefined ? "Adding…" : "Add") : "Next"}
          </Button>
        </>
      }
    >
      {err !== undefined && <PlanRefused err={err} what="Add" holder={plan.holder} />}
      {room.refusal !== undefined && (
        <Notice kind="info">
          {room.refusal} <PlanLink />
        </Notice>
      )}
      <p className="small faint">
        Step {where.number} of {where.total} — {STEP_TITLES[at]}
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
      >
        {at === "server" && (
          <>
            <Field label="Name" hint="Lowercase letters, digits and hyphens. Apps reach it under this name.">
              <TextInput value={name} onChange={setName} placeholder="github" mono required autoFocus />
            </Field>
            <Field label="Server" hint="The MCP server's address. Its host is what the toolkit may reach.">
              <TextInput
                value={url}
                onChange={setUrl}
                placeholder="https://mcp.example/mcp/"
                type="url"
                mono
                required
              />
            </Field>
            <div className="field">
              <span className="label">Acts as</span>
              <Choices
                name="scope"
                value={scope}
                onChange={setScope}
                options={[
                  {
                    value: Scope.person,
                    label: "The person who asks",
                    hint: "Each person links their own account or pastes their own key; the server sees them.",
                  },
                  {
                    value: Scope.tenant,
                    label: s.name,
                    hint: "One key for everyone, stored once by you; the server sees one identity.",
                  },
                  { value: Scope.none, label: "Nobody", hint: "The server is public and needs no key." },
                ]}
              />
            </div>
          </>
        )}
        {at === "sign-in" && (
          <>
            {scope === Scope.person && (
              <div className="field">
                <span className="label">How a person signs in to it</span>
                <Choices
                  name="scheme"
                  value={personScheme}
                  onChange={setPersonScheme}
                  options={[
                    {
                      value: Scheme.oauthRefresh,
                      label: "Linking their own account",
                      hint: "Sends them to the provider's consent screen; the platform never sees their password.",
                    },
                    {
                      value: Scheme.token,
                      label: "Pasting a key",
                      hint: "A bearer or API key of their own.",
                    },
                  ]}
                />
              </div>
            )}
            {redeeming && (
              <>
                <Notice title="Before this works: a registration at the provider">
                  Register this toolkit as its own client at the provider that protects the server, with{" "}
                  <span className="mono">{redirectUri}</span> as its redirect URI, and paste the client id
                  below. The same page is where people link. Linking sends them to the provider&apos;s consent
                  screen.
                </Notice>
                <Field label="Authorization endpoint">
                  <TextInput value={authorize} onChange={setAuthorize} type="url" mono required />
                </Field>
                <Field label="Token endpoint" hint="Its host is also what the toolkit may reach.">
                  <TextInput value={tokenEndpoint} onChange={setTokenEndpoint} type="url" mono required />
                </Field>
                <Field label="Client id" hint="This toolkit's own registration at the provider.">
                  <TextInput value={clientId} onChange={setClientId} mono required />
                </Field>
                {confidential && (
                  <Field
                    label="Client secret"
                    hint="Stored where only this toolkit reads it; never shown again."
                  >
                    <TextInput value={clientSecret} onChange={setClientSecret} type="password" required />
                  </Field>
                )}
                <Field label="Audience" hint="What the redeemed token is for: the server's API.">
                  <TextInput
                    value={audience}
                    onChange={setAudience}
                    placeholder="https://api.example"
                    mono
                    required
                  />
                </Field>
              </>
            )}
            {scope === Scope.tenant && (
              <Field
                label="Key"
                hint={`The one key everyone in ${s.name} uses. Stored where only this toolkit reads it; never shown again.`}
              >
                <TextInput value={sharedKey} onChange={setSharedKey} type="password" required />
              </Field>
            )}
            {scheme !== Scheme.none && (
              <details className="more-options">
                <summary className="small">More options</summary>
                <div className="stack">
                  {redeeming && (
                    <>
                      <div className="field">
                        <span className="label">Client type</span>
                        <Choices
                          name="client-type"
                          value={clientType}
                          onChange={setClientType}
                          options={[
                            {
                              value: ClientType.public,
                              label: "Public",
                              hint: "The browser exchanges the code itself.",
                            },
                            {
                              value: ClientType.confidential,
                              label: "Confidential",
                              hint: "Has a secret; the toolkit exchanges the code, and the secret never reaches a browser.",
                            },
                          ]}
                        />
                      </div>
                      <Field label="Scopes" hint="What consent asks for, separated by spaces.">
                        <TextInput value={scopes} onChange={setScopes} placeholder="repo read:org" mono />
                      </Field>
                    </>
                  )}
                  <>
                    <div className="field">
                      <span className="label">Where the key goes on each request</span>
                      <Choices
                        name="attach"
                        value={attach}
                        onChange={setAttach}
                        options={[
                          { value: "bearer", label: "Authorization: Bearer" },
                          { value: "header", label: "A header of its own" },
                          { value: "query", label: "A query parameter" },
                        ]}
                      />
                    </div>
                    {attach !== "bearer" && (
                      <Field label={attach === "header" ? "Header name" : "Parameter name"}>
                        <TextInput
                          value={attachName}
                          onChange={setAttachName}
                          placeholder={attach === "header" ? "X-API-Key" : "api_key"}
                          mono
                          required
                        />
                      </Field>
                    )}
                  </>
                </div>
              </details>
            )}
          </>
        )}
        {at === "tools" && (
          <>
            {scope === Scope.person ? (
              <div className="field">
                <span className="label">Tools</span>
                <textarea
                  className="input mono"
                  rows={6}
                  value={declared}
                  placeholder={
                    'one upstream name per line, or a JSON array of {"name", "description", "input-schema"}'
                  }
                  onChange={(e) => {
                    setDeclared(e.target.value);
                  }}
                />
                <span className="hint">
                  The server lists nothing to a toolkit that is not yet signed in as someone, so its tools are
                  declared here: link once yourself, read the list, and write the subset.
                </span>
              </div>
            ) : (
              <Field
                label="Tools"
                hint="The tools to offer, by their upstream names, or * for every one the server lists."
              >
                <TextInput value={tools} onChange={setTools} mono required />
              </Field>
            )}
            <details className="more-options">
              <summary className="small">More options</summary>
              <div className="stack">
                <Field label="Instructions" hint="Optional. Shown to a person before they link or paste.">
                  <TextInput value={instructions} onChange={setInstructions} />
                </Field>
              </div>
            </details>
            {problem !== undefined && <p className="small bad">{problem}</p>}
            <p className="small faint">
              Image <span className="mono">{image}</span>, {RESOURCES.cpus} vCPU · {RESOURCES.memory_mib} MiB;
              may reach{" "}
              {composed instanceof Error || egressOf(composed).length === 0 ? (
                "nothing until a server is named"
              ) : (
                <span className="mono">{egressOf(composed).join(", ")}</span>
              )}
              .
            </p>
          </>
        )}
      </form>
    </FormPage>
  );
}
